import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ChartMultiDataList } from '../../models/ChartDataList';
import { LineChartData } from '../../models/lineChartData';
import { Sensor } from '../../models/weatherstation/sensor';
import { SensorData } from '../../models/weatherstation/sensorData';
import { DynamicTranslateService } from '../../services/dynamic-translate.service';
import { NumberService } from '../../services/number.service';

export interface MultiLineSeries {
  label: string;
  data: number[];
  color?: string;
  fill?: boolean;
}

export interface MultiLineChartData {
  labels: string[];
  series: MultiLineSeries[];
}

// 'step' draws right-angle segments (series.step = 'end'): correct rendering
// for on/off states (valves, relays) where interpolation would lie.
type ChartStyle = 'basic' | 'smooth' | 'smoothArea' | 'area' | 'step';

/**
 * Apache ECharts line chart with four input modes (priority order):
 *   1. `sensor`                  – single series with limit threshold
 *   2. `lineChartData`           – legacy LineChartData payload
 *   3. `lineChartMultiDataList`  – multi-series time data (epoch keys)
 *   4. `chartData`               – multi-series with category labels
 */
@Component({
  selector: 'urban-echarts-linechart',
  templateUrl: './echarts-linechart.component.html',
  styleUrls: ['./echarts-linechart.component.scss'],
  standalone: false
})
export class EchartsLinechartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() public linechartId: string = '';
  @Input() public sensor: Sensor;
  @Input() public lineChartData: LineChartData;
  @Input() public lineChartMultiDataList: ChartMultiDataList = {};
  @Input() public propertiesList: string[] = [];
  @Input() public multiChartTitle: string = '';
  @Input() public chartData: MultiLineChartData;
  @Input() public legendPosition: 'top' | 'bottom' = 'top';
  @Input() public maxValueDescription: string;
  @Input() public valueDescription: string;
  @Input() public startDate: number;
  @Input() public endDate: number;
  @Input() public darkThemeActive: boolean;
  @Input() public passedHeight: number = 550;
  @Input() public chartStyle: ChartStyle = 'smoothArea';

  @ViewChild('linechartContainer', { static: true })
  private container: ElementRef<HTMLDivElement>;

  private readonly datePipe = inject(DatePipe);
  private readonly dynamicTranslate = inject(DynamicTranslateService);
  private readonly nService = inject(NumberService);
  private readonly destroyRef = inject(DestroyRef);

  private chart: any;
  private echarts: any;
  private translationsReady: boolean = false;
  private resizeObserver: ResizeObserver | undefined;
  private timePeriod: string = 'GENERAL.LAST_24H';
  private translatedMax: string;
  private translatedValue: string;
  private translatedTimePeriod: string;

  private lightPrimary: string = '#0971ce';
  private darkPrimary: string = '#0971ce';
  private chartColors: string[] = [];

  public noDataOnChart: boolean = false;

  // Below this container width the period title no longer fits beside the legend.
  private readonly compactWidthPx: number = 560;
  private compactLayout: boolean = false;

  @HostListener('window:resize')
  public onResize(): void {
    this.onContainerResize();
  }

  public async ngAfterViewInit(): Promise<void> {
    if (!this.echarts) {
      const mod: any = await import('echarts');
      this.echarts = mod?.default ?? mod;
    }
    this.lightPrimary = this.cssVar('--custom-light-primary-main-color', '#0971ce');
    this.darkPrimary = this.cssVar('--custom-dark-primary-main-color', '#0971ce');
    this.chartColors = this.resolveChartColors();

    if (typeof ResizeObserver !== 'undefined' && this.container) {
      this.resizeObserver = new ResizeObserver(() => this.onContainerResize());
      this.resizeObserver.observe(this.container.nativeElement);
    }

    this.loadTranslations();
  }

  public ngOnChanges(): void {
    if (this.echarts && this.translationsReady) {
      this.drawChart();
    }
  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.chart?.dispose();
    this.chart = undefined;
  }

  // ---------------------------------------------------------------------------
  // Translations
  // ---------------------------------------------------------------------------

  private loadTranslations(): void {
    const phrases: string[] = [
      this.maxValueDescription,
      this.valueDescription,
      this.timePeriod
    ].filter(Boolean);

    if (phrases.length === 0) {
      this.translatedMax = this.maxValueDescription;
      this.translatedValue = this.valueDescription;
      this.translatedTimePeriod = '';
      this.translationsReady = true;
      this.drawChart();
      return;
    }

    this.dynamicTranslate.getDynamicTranslations(phrases)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: { [key: string]: string }) => {
        this.translatedMax = res[this.maxValueDescription] ?? this.maxValueDescription;
        this.translatedValue = res[this.valueDescription] ?? this.valueDescription;
        this.translatedTimePeriod = res[this.timePeriod] ?? this.timePeriod;
        this.translationsReady = true;
        this.drawChart();
      });
  }

  // ---------------------------------------------------------------------------
  // Drawing
  // ---------------------------------------------------------------------------

  private onContainerResize(): void {
    if (!this.chart) {
      return;
    }
    if (this.isCompactWidth() !== this.compactLayout) {
      this.drawChart();
    }
    this.chart.resize();
  }

  private isCompactWidth(): boolean {
    const width: number = this.container?.nativeElement.clientWidth ?? 0;
    return width > 0 && width < this.compactWidthPx;
  }

  private drawChart(): void {
    if (!this.echarts || !this.container) {
      return;
    }

    this.compactLayout = this.isCompactWidth();

    if (!this.chart) {
      this.chart = this.echarts.init(this.container.nativeElement, null, { renderer: 'canvas' });
    }

    let option: any;
    if (this.sensor?.Data?.length > 0) {
      option = this.buildSensorOption();
    } else if (this.lineChartData?.Elements?.length > 0) {
      option = this.buildLineChartDataOption();
    } else if (this.lineChartMultiDataList && Object.keys(this.lineChartMultiDataList).length > 0) {
      option = this.buildMultiDataListOption();
    } else if (this.chartData?.labels?.length > 0 && this.chartData?.series?.length > 0) {
      option = this.buildCategoryDataOption();
    } else {
      this.noDataOnChart = true;
      this.chart.clear();
      return;
    }

    this.noDataOnChart = false;
    this.chart.setOption(option, true);
  }

  // ---------------------------------------------------------------------------
  // Series builders
  // ---------------------------------------------------------------------------

  private buildSensorOption(): any {
    const unit: string = this.normalizeUnit(this.sensor.Unit);
    const limit: number | undefined =
      this.sensor.Limit !== 'N.A.' && this.nService.isNumber(this.sensor.Limit)
        ? +this.sensor.Limit
        : undefined;

    const limitLabel: string = `${this.translatedMax} (${this.formatValue(this.sensor.Limit, unit)})`;
    const points: [number, number][] = [];
    const exceeding: [number, number][] = [];

    this.sensor.Data.forEach((d: SensorData) => {
      const ts: number = this.toLocalTimestamp(d.Created);
      const value: number = Number(d.Value);
      points.push([ts, value]);
      if (limit !== undefined && this.nService.isNumber(d.Value) && +d.Value >= limit) {
        exceeding.push([ts, value]);
      }
    });

    const legend: string[] = [];
    if (this.translatedValue) {
      legend.push(this.translatedValue);
    }
    if (limit !== undefined) {
      legend.push(limitLabel);
    }

    return this.buildOption({
      legend,
      xAxisTitle: this.buildAxisTitle(),
      yAxisFormatter: this.unitFormatter(unit),
      tooltipFormatter: this.timeTooltipFormatter(unit),
      series: this.buildLimitedSeries(points, exceeding, limit, limitLabel)
    });
  }

  private buildLineChartDataOption(): any {
    const unit: string = this.normalizeUnit(this.lineChartData.Unit);
    const limit: number = this.lineChartData.Limit;
    const limitLabel: string = `${this.translatedMax} (${this.formatValue(limit.toString(), unit)})`;
    const points: [number, number][] = [];
    const exceeding: [number, number][] = [];

    this.lineChartData.Elements.forEach(e => {
      const ts: number = this.toLocalTimestamp(e.Date);
      const value: number = +e.Value;
      points.push([ts, value]);
      if (value >= limit) {
        exceeding.push([ts, value]);
      }
    });

    return this.buildOption({
      legend: [this.translatedValue, limitLabel],
      xAxisTitle: this.buildAxisTitle(),
      yAxisFormatter: this.unitFormatter(unit),
      tooltipFormatter: this.timeTooltipFormatter(unit),
      series: this.buildLimitedSeries(points, exceeding, limit, limitLabel)
    });
  }

  private buildMultiDataListOption(): any {
    const palette: string[] = this.buildPalette();
    const properties: string[] = this.propertiesList?.length
      ? this.propertiesList
      : Object.keys(this.lineChartMultiDataList[Object.keys(this.lineChartMultiDataList)[0]] ?? {});

    const isSmooth: boolean = this.isSmooth();
    const sortedEpochs: number[] = Object.keys(this.lineChartMultiDataList)
      .map(k => +k)
      .sort((a, b) => a - b);

    const series: any[] = properties.map((property: string, index: number) => {
      const points: [number, number][] = sortedEpochs.reduce(
        (acc: [number, number][], epoch: number) => {
          const value: number = this.lineChartMultiDataList[`${epoch}`]?.[property];
          if (value !== undefined && value !== null) {
            acc.push([this.toLocalTimestamp(epoch), value]);
          }
          return acc;
        },
        []
      );

      const color: string = palette[index % palette.length];
      // Show point markers only on sparse datasets to avoid a wall of dots.
      const showSymbol: boolean = points.length > 0 && points.length <= 80;

      return {
        name: property,
        type: 'line',
        data: points,
        showSymbol,
        symbol: 'circle',
        symbolSize: 6,
        sampling: 'lttb',
        smooth: isSmooth,
        smoothMonotone: 'x',
        step: this.isStep() ? 'end' : undefined,
        connectNulls: false,
        lineStyle: { width: 2, color, cap: 'round', join: 'round' },
        itemStyle: { color, borderColor: '#fff', borderWidth: 1.5 },
        areaStyle: this.isStep() ? this.buildAreaStyle(color) : undefined,
        emphasis: { focus: 'series', scale: 1.4, lineStyle: { width: 2.5 } }
      };
    });

    return this.buildOption({
      legend: properties,
      xAxisTitle: this.multiChartTitle ?? '',
      yAxisFormatter: undefined,
      tooltipFormatter: this.timeTooltipFormatter(),
      series
    });
  }

  private buildCategoryDataOption(): any {
    const isSmooth: boolean = this.isSmooth();
    const hasArea: boolean = this.hasArea();
    const palette: string[] = this.buildPalette();

    const series: any[] = this.chartData.series.map((s, index) => {
      const color: string = s.color ?? palette[index % palette.length];
      // Single-series charts get an area fill by default for visual impact;
      // multi-series default to plain lines unless the caller opts in.
      const fill: boolean = s.fill ?? (hasArea && this.chartData.series.length === 1);
      return {
        name: s.label,
        type: 'line',
        data: s.data,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 6,
        sampling: 'lttb',
        smooth: isSmooth,
        smoothMonotone: 'x',
        lineStyle: { width: 2, color, cap: 'round', join: 'round' },
        itemStyle: { color, borderColor: '#fff', borderWidth: 1.5 },
        areaStyle: fill ? this.buildAreaStyle(color) : undefined,
        emphasis: { focus: 'series', scale: 1.4, lineStyle: { width: 2.5 } }
      };
    });

    return this.buildOption({
      legend: this.chartData.series.map(s => s.label),
      xAxisTitle: '',
      xAxisType: 'category',
      xAxisData: this.chartData.labels,
      yAxisFormatter: undefined,
      tooltipFormatter: this.categoryTooltipFormatter(),
      series,
      showDataZoom: false,
      legendPosition: this.legendPosition,
      yAxisMin: 0
    });
  }

  private buildLimitedSeries(
    points: [number, number][],
    exceeding: [number, number][],
    limit: number | undefined,
    limitLabel: string
  ): any[] {
    const primary: string = this.darkThemeActive ? this.darkPrimary : this.lightPrimary;
    const exceedColor: string = this.chartColors[0] || '#e74c3c';
    const isSmooth: boolean = this.isSmooth();
    const hasArea: boolean = this.hasArea();

    const series: any[] = [{
      name: this.translatedValue,
      type: 'line',
      data: points,
      showSymbol: points.length === 1,
      symbol: 'circle',
      symbolSize: 7,
      sampling: 'lttb',
      smooth: isSmooth,
      smoothMonotone: 'x',
      lineStyle: { width: 2.5, color: primary, cap: 'round', join: 'round' },
      itemStyle: { color: primary, borderColor: '#fff', borderWidth: 2 },
      areaStyle: hasArea ? this.buildAreaStyle(primary) : undefined,
      emphasis: { focus: 'series', scale: 1.4, lineStyle: { width: 3 } },
      markPoint: exceeding.length ? {
        symbol: 'circle',
        symbolSize: 9,
        itemStyle: { color: exceedColor, borderColor: '#fff', borderWidth: 2 },
        data: exceeding.map(p => ({ coord: p })),
        label: { show: false },
        tooltip: { show: false }
      } : undefined,
      z: 3
    }];

    if (limit !== undefined) {
      series.push({
        name: limitLabel,
        type: 'line',
        data: points.map(p => [p[0], limit]),
        showSymbol: false,
        lineStyle: { width: 1.5, color: exceedColor, type: 'dashed' },
        itemStyle: { color: exceedColor },
        silent: true,
        z: 1
      });
    }

    return series;
  }

  // ---------------------------------------------------------------------------
  // Tooltips
  // ---------------------------------------------------------------------------

  private timeTooltipFormatter(unit: string = ''): (params: any) => string {
    return (params: any) => {
      const arr: any[] = Array.isArray(params) ? params : [params];
      if (!arr.length) {
        return '';
      }
      const time: string = this.datePipe.transform(arr[0].axisValue, 'dd/MM/yyyy HH:mm') ?? '';
      const lines: string[] = arr.map(p => {
        const value: any = p.data?.[1] ?? p.value?.[1] ?? p.value;
        let formatted: string = unit ? `${value}${unit}` : `${value}`;
        if (unit && unit.toLowerCase() === ' deg') {
          formatted = `${formatted} (${this.degreesToCardinal(Number(value))})`;
        }
        return `${p.marker} <b>${p.seriesName}</b>: ${formatted}`;
      });
      return `${time}<br/>${lines.join('<br/>')}`;
    };
  }

  private categoryTooltipFormatter(): (params: any) => string {
    return (params: any) => {
      const arr: any[] = Array.isArray(params) ? params : [params];
      if (!arr.length) {
        return '';
      }
      const label: string = `${arr[0].axisValueLabel ?? arr[0].name ?? ''}`;
      const lines: string[] = arr.map(p => {
        const value: any = p.data?.[1] ?? p.value;
        return `${p.marker} <b>${p.seriesName}</b>: ${value}`;
      });
      return `${label}<br/>${lines.join('<br/>')}`;
    };
  }

  // ---------------------------------------------------------------------------
  // Shared option builder
  // ---------------------------------------------------------------------------

  private buildOption(parts: {
    legend: string[];
    xAxisTitle: string;
    yAxisFormatter: ((value: number) => string) | undefined;
    tooltipFormatter: (params: any) => string;
    series: any[];
    xAxisType?: 'time' | 'category';
    xAxisData?: string[];
    showDataZoom?: boolean;
    legendPosition?: 'top' | 'bottom';
    yAxisMin?: number | null;
  }): any {
    const xAxisType: 'time' | 'category' = parts.xAxisType ?? 'time';
    const showDataZoom: boolean = parts.showDataZoom ?? true;
    const legendPosition: 'top' | 'bottom' = parts.legendPosition ?? 'top';
    const yAxisMin: number | null = parts.yAxisMin ?? 0;
    const textColor: string = this.darkThemeActive ? '#FFFFFF' : '#505566';
    const subtleText: string = this.darkThemeActive ? 'rgba(255,255,255,0.65)' : '#7b85a6';
    const splitLineColor: string = this.darkThemeActive ? 'rgba(255,255,255,0.08)' : '#eef2f7';
    const axisLineColor: string = this.darkThemeActive ? 'rgba(255,255,255,0.20)' : '#dde3ee';
    const primary: string = this.darkThemeActive ? this.darkPrimary : this.lightPrimary;
    const stackTitleUnderLegend: boolean = this.compactLayout && !!parts.xAxisTitle && legendPosition === 'top';

    return {
      animation: true,
      animationDuration: 900,
      animationEasing: 'cubicOut',
      animationDurationUpdate: 350,
      animationEasingUpdate: 'cubicInOut',
      backgroundColor: 'transparent',
      textStyle: { fontFamily: 'Poppins', color: textColor },

      title: parts.xAxisTitle ? {
        text: parts.xAxisTitle,
        ...(stackTitleUnderLegend ? { left: 'center', top: 30 } : { right: 12, top: 6 }),
        textStyle: { color: subtleText, fontFamily: 'Poppins', fontSize: 11, fontWeight: 'normal' }
      } : undefined,

      legend: {
        ...(legendPosition === 'top' ? { top: 6 } : { bottom: 6 }),
        left: 'center',
        type: 'scroll',
        icon: 'roundRect',
        itemWidth: 14,
        itemHeight: 8,
        itemGap: 18,
        textStyle: { color: textColor, fontFamily: 'Poppins', fontSize: 12 },
        pageIconColor: primary,
        pageTextStyle: { color: textColor },
        data: parts.legend
      },

      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: { color: primary, width: 1, type: 'dashed', opacity: 0.6 }
        },
        formatter: parts.tooltipFormatter,
        backgroundColor: this.darkThemeActive ? 'rgba(22,26,35,0.96)' : 'rgba(255,255,255,0.98)',
        borderColor: this.darkThemeActive ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)',
        borderWidth: 1,
        padding: [8, 12],
        extraCssText: 'box-shadow: 0 6px 20px rgba(0,0,0,0.12); border-radius: 8px;',
        textStyle: { color: textColor, fontFamily: 'Poppins', fontSize: 12 }
      },

      grid: {
        left: 56,
        right: 28,
        top: legendPosition === 'top' ? (stackTitleUnderLegend ? 76 : 52) : 28,
        bottom: showDataZoom ? 78 : (legendPosition === 'bottom' ? 56 : 40)
      },

      xAxis: {
        type: xAxisType,
        boundaryGap: xAxisType === 'category',
        ...(xAxisType === 'category' ? { data: parts.xAxisData ?? [] } : {}),
        axisLabel: {
          color: textColor,
          fontFamily: 'Poppins',
          fontSize: 11,
          hideOverlap: true,
          margin: 10,
          ...(xAxisType === 'time' ? { formatter: this.xAxisFormatter() } : {})
        },
        axisLine: { lineStyle: { color: axisLineColor } },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { color: splitLineColor } }
      },

      yAxis: {
        type: 'value',
        ...(yAxisMin !== null ? { min: yAxisMin } : {}),
        axisLabel: {
          color: textColor,
          fontFamily: 'Poppins',
          fontSize: 11,
          formatter: parts.yAxisFormatter
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: splitLineColor, type: 'dashed' } }
      },

      dataZoom: showDataZoom ? [
        { type: 'inside', xAxisIndex: 0, throttle: 50 },
        {
          type: 'slider',
          xAxisIndex: 0,
          height: 22,
          bottom: 12,
          showDetail: false,
          borderColor: 'transparent',
          backgroundColor: this.darkThemeActive ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
          dataBackground: {
            lineStyle: { color: primary, opacity: 0.45, width: 1 },
            areaStyle: { color: primary, opacity: 0.12 }
          },
          selectedDataBackground: {
            lineStyle: { color: primary, opacity: 0.9 },
            areaStyle: { color: primary, opacity: 0.25 }
          },
          fillerColor: this.hexToRgba(primary, this.darkThemeActive ? 0.18 : 0.12),
          handleStyle: { color: primary, borderColor: '#fff', borderWidth: 1 },
          moveHandleStyle: { color: primary, opacity: 0.6 },
          textStyle: { color: 'transparent' }
        }
      ] : undefined,

      series: parts.series
    };
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private isSmooth(): boolean {
    return this.chartStyle === 'smooth' || this.chartStyle === 'smoothArea';
  }

  private isStep(): boolean {
    return this.chartStyle === 'step';
  }

  private hasArea(): boolean {
    return this.chartStyle === 'area' || this.chartStyle === 'smoothArea';
  }

  private buildAreaStyle(color: string): any {
    return {
      opacity: 0.85,
      color: {
        type: 'linear',
        x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
          { offset: 0, color: this.hexToRgba(color, this.darkThemeActive ? 0.55 : 0.35) },
          { offset: 1, color: this.hexToRgba(color, 0) }
        ]
      }
    };
  }

  private hexToRgba(color: string, alpha: number): string {
    if (!color) {
      return `rgba(9,113,206,${alpha})`;
    }
    const trimmed: string = color.trim();
    if (trimmed.startsWith('rgb')) {
      return trimmed.replace(/rgba?\(([^)]+)\)/, (_m, body: string) => {
        const parts: string[] = body.split(',').map(p => p.trim()).slice(0, 3);
        return `rgba(${parts.join(',')},${alpha})`;
      });
    }
    if (trimmed.startsWith('#')) {
      let hex: string = trimmed.slice(1);
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
      }
      const r: number = parseInt(hex.substring(0, 2), 16);
      const g: number = parseInt(hex.substring(2, 4), 16);
      const b: number = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r},${g},${b},${alpha})`;
    }
    return trimmed;
  }

  private buildAxisTitle(): string {
    if (this.startDate && this.endDate) {
      return `${this.pipeDate(this.startDate)} - ${this.pipeDate(this.endDate)}`;
    }
    return this.translatedTimePeriod ?? '';
  }

  private xAxisFormatter(): (value: number) => string {
    return (value: number) => {
      const top: string = this.datePipe.transform(value, 'HH:mm') ?? '';
      const bottom: string = this.datePipe.transform(value, 'dd/MM/yy') ?? '';
      return `${top}\n${bottom}`;
    };
  }

  private unitFormatter(unit: string): ((v: number) => string) | undefined {
    return unit ? (v: number) => `${v}${unit}` : undefined;
  }

  private normalizeUnit(unit: string | undefined): string {
    if (!unit || unit === '' || unit === '%') {
      return unit ?? '';
    }
    return unit.startsWith(' ') ? unit : ` ${unit}`;
  }

  private formatValue(raw: string | number, unit: string): string {
    if (raw === null || raw === undefined) {
      return '';
    }
    return unit ? `${raw}${unit}` : `${raw}`;
  }

  private toLocalTimestamp(epochSeconds: number): number {
    const utc: Date = new Date(epochSeconds * 1000);
    return new Date(
      utc.getFullYear(), utc.getMonth(), utc.getDate(),
      utc.getHours(), utc.getMinutes(), utc.getSeconds(), utc.getMilliseconds()
    ).getTime();
  }

  private pipeDate(epochSeconds: number): string {
    return this.datePipe.transform(epochSeconds * 1000, 'short') ?? '';
  }

  private degreesToCardinal(degrees: number): string {
    const directions: string[] = [
      'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
      'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
    ];
    return directions[Math.round((degrees + 11.25) / 22.5) % 16];
  }

  private buildPalette(): string[] {
    const primary: string = this.darkThemeActive ? this.darkPrimary : this.lightPrimary;
    return [primary, ...this.chartColors].filter(Boolean);
  }

  private resolveChartColors(): string[] {
    return [
      this.cssVar('--custom-chart-red-color', '#e74c3c'),
      this.cssVar('--custom-chart-yellow-color', '#f1c40f'),
      this.cssVar('--custom-chart-green-color', '#2ecc71'),
      this.cssVar('--custom-chart-1-color', '#9b59b6'),
      this.cssVar('--custom-chart-2-color', '#1abc9c'),
      this.cssVar('--custom-chart-3-color', '#e67e22'),
      this.cssVar('--custom-chart-4-color', '#34495e')
    ];
  }

  private cssVar(name: string, fallback: string): string {
    const v: string = window.getComputedStyle(document.body).getPropertyValue(name).trim();
    return v || fallback;
  }
}
